import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { MatMenuPanel } from '@angular/material/menu';

import { SnAbilityService } from '@lib/ability';
import { SnContentType, SnTag } from '@lib/api/entities';
import { SnCollection } from '@lib/common/models';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Component({
  selector: 'sn-tag-edit-list',
  templateUrl: './tag-edit-list.component.html',
  styleUrls: ['./tag-edit-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnTagEditListComponent implements OnDestroy {
  @Input()
  public contentType: SnContentType;

  @Input()
  public canChange = true;

  @Input()
  get tags(): SnCollection<SnTag> {
    return this._tags;
  }
  set tags(value: SnCollection<SnTag>) {
    this._tags = new SnCollection<SnTag>(...value);
  }

  @Output()
  public readonly tagsChanged = new EventEmitter<SnTag[]>();

  public readonly subject = 'tags/tag';
  public readonly canUserAddTag: boolean;
  public readonly canUserChangeTag: boolean;
  public readonly canUserDeleteTag: boolean;

  private _tags: SnCollection<SnTag>;

  @ViewChild('menu')
  private readonly _menu: MatMenuPanel;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
    private readonly _ability: SnAbilityService,
  ) {
    this.canUserAddTag = this._ability.can({
      subject: this.subject,
      action: 'add',
    });
    this.canUserChangeTag = this._ability.can({
      subject: this.subject,
      action: 'change',
    });
    this.canUserDeleteTag = this._ability.can({
      subject: this.subject,
      action: 'delete',
    });
  }

  public ngOnDestroy(): void {
    this.tagsChanged.complete();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackTag(index: number, item: SnTag): any {
    return item.id;
  }

  public onTagRemoved(tag: SnTag): void {
    this._dialogService
      .confirm({
        message: t('tags.removeTagConfirmation'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((response: boolean) => {
        if (response) {
          this.tags.delete(tag);
          this.tagsChanged.emit(this.tags);
          this._toastrService.success(t('tags.tagRemoved'));
        }
      });
  }

  public onTagsSelectionChanged(tags: SnTag[]): void {
    this.tagsChanged.emit(tags);
    // this.tags = new SnCollection<SnTag>(...tags);
    this._menu.close.emit('click');
  }
}
